import { useMemo, useState } from 'react';
import { toast } from '../../reusable/Toast/toastStore';
import { debounce } from '../../utilities/debounce';
import { t } from '../../i18n/i18n';
import {
  affiliateErrorMessage,
  useCreateAffiliateRequestMutation,
  useGetAffiliateRequestOptionsQuery,
  useGetAffiliateRequestsQuery,
  useGetAffiliateRequestsSummaryQuery,
} from '../../store/api/affiliateApi';

const PAGE_SIZE = 10;
const SEARCH_DELAY = 300;

/** The three chips, in the order the design draws them. */
const STATUSES = [
  { label: 'Approved', tone: 'success' },
  { label: 'Processing', tone: 'info' },
  { label: 'Rejected', tone: 'danger' },
];

/** A request status's chip tone — the chips' own colours, so row and chip agree. */
export const requestStatusTone = (status) =>
  STATUSES.find((s) => s.label.toLowerCase() === String(status ?? '').toLowerCase())?.tone;

/** "$5,000" from a challenge's "5000". */
export const formatBalance = (balance) => {
  const n = Number(balance);
  return Number.isFinite(n) && n > 0 ? `$${n.toLocaleString('en-US')}` : '—';
};

/**
 * Everything the Giveaway and Personal Account screens read and send — they
 * are one API (`/affiliate/requests/`) split by `request_type`, and drew the
 * same chips, history and form plumbing twice before this. Each screen keeps
 * its own form fields and columns.
 *
 *   options      platforms, challenges and limits for the form
 *   eligibility  whether this user may request at all, and why not
 *   statusCounts the chips' counts, from `/requests/summary`
 *   table        the history's rows and paging, filtered server-side
 *   submit       POST, with the toasts
 */
export default function useAffiliateRequests(requestType) {
  /* ---- form options ---- */
  const { data: options, isLoading: isOptionsLoading } = useGetAffiliateRequestOptionsQuery();
  const { data: summary } = useGetAffiliateRequestsSummaryQuery();

  const challengeOptions = useMemo(
    () =>
      (options?.challenges ?? []).map((c) => ({
        label: `${c.name} · ${formatBalance(c.account_balance)}`,
        value: c.id,
      })),
    [options]
  );
  const platformOptions = useMemo(
    () => (options?.platforms ?? []).map((p) => ({ label: p.label, value: p.value })),
    [options]
  );

  const eligibility = summary?.eligibility ?? options?.eligibility ?? null;

  /* ---- chips ---- */
  const statusCounts = useMemo(() => {
    const counts = summary?.counts?.[requestType] ?? {};
    return STATUSES.map((s) => ({ ...s, count: counts[s.label] ?? 0 }));
  }, [summary, requestType]);

  /* ---- history ---- */
  const [status, setStatus] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const onSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value.trim());
        setPage(1);
      }, SEARCH_DELAY),
    []
  );

  const {
    data: list,
    isLoading,
    isFetching,
    isError,
  } = useGetAffiliateRequestsQuery({
    request_type: requestType,
    page,
    page_size: PAGE_SIZE,
    ...(status && { status }),
    ...(search && { search }),
  });

  const rows = list?.results ?? [];
  const table = {
    rows,
    total: list?.count ?? rows.length,
    page,
    pageSize: PAGE_SIZE,
    onPageChange: setPage,
    onSearch,
    loading: isLoading || isFetching,
    emptyMessage: isError
      ? t('Could not load your requests')
      : status || search
        ? t('No requests match your search or filter')
        : t('No requests yet'),
  };

  const setStatusFilter = (next) => {
    setStatus(next);
    setPage(1);
  };

  /* ---- submit ---- */
  const [create, { isLoading: isSubmitting }] = useCreateAffiliateRequestMutation();

  /** Resolves true once the request is in, so the form knows to reset. */
  const submit = async (payload, successTitle) => {
    try {
      const res = await create({ request_type: requestType, ...payload }).unwrap();
      toast.success(
        successTitle,
        res?.message ??
          t('Request {{id}} has been submitted for admin approval.', { id: res?.request?.id ?? '' })
      );
      return true;
    } catch (err) {
      toast.error(t('Could not submit request'), affiliateErrorMessage(err, t('Failed to submit request. Please try again.')));
      return false;
    }
  };

  return {
    limits: options?.limits ?? {},
    challengeOptions,
    platformOptions,
    isOptionsLoading,
    eligibility,
    statusCounts,
    statusFilter: status,
    setStatusFilter,
    table,
    submit,
    isSubmitting,
  };
}
